// Site-wide search, opened from the nav or with Ctrl+K (⌘K on a Mac). An
// accessible combobox: type to search, arrow keys to move through the
// results, Enter to open one, Escape to close.
//
// Results carry logos, avatars and two lines of text, which native
// <select>/<option> can't show, so this follows the ARIA combobox pattern
// with a custom listbox instead.
/* oxlint-disable jsx-a11y/prefer-tag-over-role */
import { useEffect, useId, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router'
import { BadgeCheck, Building2, CalendarDays, CornerDownLeft, Search, UserRound, UserSearch, UsersRound } from 'lucide-react'
import { GAME_ROLE_LABELS } from '@pred-hub/shared/constants'
import { TeamMark } from '../brand/TeamMark.jsx'
import { Avatar } from '../ui/Avatar.jsx'
import { api } from '../../lib/api.js'
import { cn } from '../../lib/format.js'
import { formatDate } from '../../lib/time.js'

const SHORTCUTS = [
  { key: 'go-events', label: 'Browse events', hint: 'Cups, leagues and invitationals', to: '/events', icon: CalendarDays },
  { key: 'go-teams', label: 'Browse teams', hint: 'Every team on Pred Hub', to: '/teams', icon: UsersRound },
  { key: 'go-players', label: 'Browse players', hint: 'Everyone on Pred Hub', to: '/players', icon: UserRound },
  { key: 'go-free-agents', label: 'Find free agents', hint: 'Players looking for a team', to: '/free-agents', icon: UserSearch },
  { key: 'go-orgs', label: 'Browse organizers', hint: 'Who runs events', to: '/organizers', icon: Building2 },
]

const STATUS = { live: 'Live', upcoming: 'Upcoming', completed: 'Finished' }

// Results as groups of options, each with where it goes.
function toGroups(data) {
  if (!data) return []
  return [
    ['Events', data.events.map((e) => ({ key: `e-${e.slug}`, to: `/e/${e.slug}`, label: e.name, hint: `${e.org.name} · ${STATUS[e.status] ?? e.status} · ${formatDate(e.startsAt)}`, event: e }))],
    ['Teams', data.teams.map((t) => ({ key: `t-${t.slug}`, to: `/t/${t.slug}`, label: t.name, hint: `${t.tag} · ${t.region}`, team: t }))],
    ['Players', data.players.map((p) => ({ key: `p-${p.handle}`, to: `/p/${p.handle}`, label: p.ign, hint: [p.displayName, GAME_ROLE_LABELS[p.mainRole]].filter(Boolean).join(' · '), player: p }))],
    ['Organizers', data.orgs.map((o) => ({ key: `o-${o.slug}`, to: `/o/${o.slug}`, label: o.name, hint: o.verified ? 'Verified organizer' : 'Organizer', org: o }))],
  ].filter(([, items]) => items.length)
}

function OptionIcon({ option }) {
  if (option.team) return <TeamMark team={option.team} size="sm" />
  if (option.event) return <TeamMark team={{ tag: option.event.name.slice(0, 2).toUpperCase(), accent: option.event.accent, logoUrl: option.event.logoUrl }} size="sm" />
  if (option.org) return <TeamMark team={{ tag: option.org.name.slice(0, 2).toUpperCase(), accent: option.org.accent, logoUrl: option.org.logoUrl }} size="sm" />
  if (option.player) return <Avatar src={option.player.avatarUrl} name={option.player.ign} size="sm" decorative />
  const Icon = option.icon
  return (
    <span className="grid size-8 place-items-center rounded-lg bg-white/5 ring-1 ring-white/10 ring-inset">
      <Icon aria-hidden="true" className="size-4 text-ink-muted" />
    </span>
  )
}

function Palette({ onClose }) {
  const navigate = useNavigate()
  const id = useId()
  const input = useRef(null)
  const [query, setQuery] = useState('')
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(false)
  const [active, setActive] = useState(0)
  const term = query.trim()

  useEffect(() => {
    input.current?.focus()
  }, [])

  useEffect(() => {
    if (term.length < 2) return undefined
    const controller = new AbortController()
    const timer = setTimeout(async () => {
      setLoading(true)
      try {
        setData(await api(`/api/search?q=${encodeURIComponent(term)}`, { signal: controller.signal }))
        setActive(0)
      } catch (err) {
        if (err.name !== 'AbortError') setData(null)
      } finally {
        setLoading(false)
      }
    }, 150)
    return () => {
      clearTimeout(timer)
      controller.abort()
    }
  }, [term])

  const groups = useMemo(() => (term.length >= 2 ? toGroups(data) : [['Go to', SHORTCUTS]]), [data, term])
  const options = groups.flatMap(([, items]) => items)
  const current = options[Math.min(active, options.length - 1)]
  const open = (option) => {
    onClose()
    navigate(option.to)
  }

  function onKeyDown(e) {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setActive((i) => (options.length ? (i + 1) % options.length : 0))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setActive((i) => (options.length ? (i - 1 + options.length) % options.length : 0))
    } else if (e.key === 'Enter' && current) {
      e.preventDefault()
      open(current)
    }
  }

  const empty = term.length >= 2 && !loading && data && !options.length
  return (
    <div className="flex max-h-[min(36rem,80vh)] flex-col">
      <div className="flex items-center gap-3 border-b border-hairline px-4">
        <Search aria-hidden="true" className="size-5 shrink-0 text-ink-muted" />
        <input
          ref={input}
          role="combobox"
          aria-expanded={options.length > 0}
          aria-controls={`${id}-list`}
          aria-activedescendant={current ? `${id}-${current.key}` : undefined}
          aria-autocomplete="list"
          aria-label="Search Pred Hub"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder="Search events, teams, players and organizers"
          className="h-14 min-w-0 flex-1 bg-transparent text-base text-ink placeholder:text-ink-muted/80 focus:outline-none"
        />
        <kbd className="hidden rounded-md bg-white/5 px-1.5 py-0.5 font-sans text-[11px] text-ink-muted ring-1 ring-white/10 sm:block">Esc</kbd>
      </div>
      <div id={`${id}-list`} role="listbox" aria-label="Results" className="min-h-0 flex-1 overflow-y-auto p-2">
        {groups.map(([label, items]) => (
          <div key={label} role="group" aria-labelledby={`${id}-${label}`} className="py-1">
            <p id={`${id}-${label}`} className="px-2 pt-1 pb-1.5 text-[11px] font-semibold tracking-[0.16em] text-ink-muted uppercase">
              {label}
            </p>
            {items.map((option) => {
              const selected = current?.key === option.key
              return (
                // The input keeps focus; options are picked with the mouse or Enter.
                // oxlint-disable-next-line jsx-a11y/click-events-have-key-events
                <div
                  key={option.key}
                  id={`${id}-${option.key}`}
                  role="option"
                  tabIndex={-1}
                  aria-selected={selected}
                  onMouseMove={() => setActive(options.indexOf(option))}
                  onClick={() => open(option)}
                  className={cn('flex cursor-pointer items-center gap-3 rounded-xl px-2 py-2', selected ? 'bg-primary-500/10 ring-1 ring-primary-400/40 ring-inset' : 'hover:bg-white/5')}
                >
                  <OptionIcon option={option} />
                  <span className="min-w-0 flex-1">
                    <span className="flex items-center gap-1.5 truncate text-sm font-medium text-ink">
                      {option.label}
                      {option.org?.verified && <BadgeCheck aria-label="Verified" className="size-3.5 shrink-0 text-secondary-400" />}
                    </span>
                    <span className="block truncate text-xs text-ink-muted">{option.hint}</span>
                  </span>
                  {selected && <CornerDownLeft aria-hidden="true" className="size-4 shrink-0 text-ink-muted" />}
                </div>
              )
            })}
          </div>
        ))}
        {empty && <p className="px-3 py-8 text-center text-sm text-ink-muted">Nothing matches “{term}”.</p>}
        {term.length === 1 && <p className="px-3 pb-3 text-xs text-ink-muted">Keep typing: searches start at two letters.</p>}
      </div>
      <p className="hidden items-center gap-3 border-t border-hairline px-4 py-2 text-[11px] text-ink-muted sm:flex">
        <span>
          <kbd className="font-sans">↑</kbd> <kbd className="font-sans">↓</kbd> to move
        </span>
        <span>
          <kbd className="font-sans">Enter</kbd> to open
        </span>
        <span className="ml-auto" aria-live="polite">
          {loading ? 'Searching…' : term.length >= 2 && data ? `${options.length} results` : ''}
        </span>
      </p>
    </div>
  )
}

// The nav's search button; also listens for Ctrl+K / ⌘K anywhere.
export function SearchPalette() {
  const dialog = useRef(null)
  const [open, setOpen] = useState(false)
  const mac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform)

  useEffect(() => {
    const onKey = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault()
        setOpen(true)
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  useEffect(() => {
    const el = dialog.current
    if (!el) return
    if (open && !el.open) el.showModal()
    if (!open && el.open) el.close()
  }, [open])

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label="Search"
        aria-keyshortcuts="Control+K Meta+K"
        className="flex h-9 items-center gap-2 rounded-full text-sm text-ink-muted ring-white/10 ring-inset hover:text-ink max-lg:w-9 max-lg:justify-center max-lg:hover:bg-white/5 lg:w-56 lg:bg-white/[0.04] lg:px-3 lg:ring-1 lg:hover:bg-white/[0.07]"
      >
        <Search aria-hidden="true" className="size-4 shrink-0" />
        <span className="hidden flex-1 text-left lg:inline">Search…</span>
        <kbd className="hidden rounded bg-white/5 px-1.5 font-sans text-[11px] ring-1 ring-white/10 lg:inline">{mac ? '⌘K' : 'Ctrl K'}</kbd>
      </button>
      {/* Clicking the backdrop (the dialog itself) closes it. */}
      {/* oxlint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions */}
      <dialog
        ref={dialog}
        aria-label="Search"
        onClose={() => setOpen(false)}
        onClick={(e) => {
          if (e.target === dialog.current) setOpen(false)
        }}
        className="m-0 mx-auto mt-[10vh] w-[min(40rem,calc(100vw-2rem))] max-w-none overflow-hidden rounded-2xl bg-surface p-0 text-ink shadow-2xl ring-1 shadow-black/60 ring-white/10 backdrop:bg-black/60 backdrop:backdrop-blur-sm"
      >
        {open && <Palette onClose={() => setOpen(false)} />}
      </dialog>
    </>
  )
}
